import MuiButton from '@material-ui/core/Button';
import MuiDialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiBox from '@material-ui/core/Box';
import { useState, useEffect } from 'react';
import * as API from 'api';
import { useMutation } from 'react-query';
import MuiTable from '@material-ui/core/Table';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableContainer from '@material-ui/core/TableContainer';
import MuiPaper from '@material-ui/core/Paper';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import MuiTablePagination from '@material-ui/core/TablePagination';
import MuiTypography from '@material-ui/core/Typography';
import MuiCloseIcon from '@material-ui/icons/Close';
import { withStyles } from '@material-ui/core/styles';
import MuiExportIcon from '@material-ui/icons/CloudDownload';

const QueryLibrary = API.QueryLibrary();

const styles = theme => ({
  iconButton: {
    minWidth: 32,
    minHeight: 32,
    padding: 0,
    borderRadius: 100,
  },
  dialogHeader: {
    '& h2': {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  },
});

const QueryResults = ({ children, prompt, title, ...props }) => {
  const { classes } = props;
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState({ page: 0, rowsPerPage: 25 });
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const executeQuery = useMutation(
    ({ prompt, page, rowsPerPage }) =>
      QueryLibrary.executeQueryLibraryPrompt({
        prompt,
        page_index: page,
        page_size: rowsPerPage,
      }),
    {
      onSuccess: data => {
        setResults(data.results);
        setTotal(data.total);
        setColumns(Object.keys(data.results[0]));
        setIsLoading(false);
      },
      onError: error => {
        setOpen(false);
        alert('Unknown Error: check your query and try again');
        executeQuery.reset();
      },
    }
  );

  const exportMutation = useMutation(() => QueryLibrary.exportQueryResults({ prompt }), {
    onSuccess: () => {
      alert('Please wait for an email with the export data.');
    },
    onError: error => {
      alert('Unknown Error: check your query and try again');
      exportMutation.reset();
    },
  });

  function handleChangePage(_, page) {
    setParams({ ...params, page: page });
  }

  function handleChangeRowsPerPage({ target: { value } }) {
    setParams({ ...params, page: 0, rowsPerPage: value });
  }

  function handleClick() {
    setOpen(true);
  }
  function handleClose() {
    setOpen(false);
  }

  function handleExport() {
    exportMutation.mutate({ prompt });
  }

  function formatValue(value) {
    if (value === null || value === undefined) {
      return '-';
    } else if (typeof value === 'object') {
      if (value instanceof Date) {
        return value.toISOString();
      } else {
        return JSON.stringify(value);
      }
    }
    return value.toString();
  }

  useEffect(() => {
    if (open) {
      executeQuery.mutate({ prompt, page: params.page, rowsPerPage: params.rowsPerPage });
    }
  }, [params, open]);

  return (
    <MuiBox>
      <MuiBox>
        <MuiButton
          variant="text"
          color="primary"
          className={classes.iconButton}
          onClick={handleClick}
          disabled={!prompt}>
          {children}
        </MuiButton>
        <MuiDialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title" fullWidth maxWidth="lg">
          <MuiDialogTitle id="form-dialog-title" className={classes.dialogHeader}>
            <MuiBox>
              <MuiTypography variant="h6">Query results</MuiTypography>
              <MuiTypography variant="body1">{title}</MuiTypography>
            </MuiBox>
            <MuiButton
              variant="contained"
              size="small"
              startIcon={<MuiExportIcon />}
              onClick={handleExport}
              disabled={exportMutation.isLoading || !prompt}>
              {exportMutation.isLoading ? 'Exporting...' : 'Export CSV'}
            </MuiButton>
            <MuiButton variant="text" color="primary" onClick={handleClose} className={classes.iconButton}>
              <MuiCloseIcon />
            </MuiButton>
          </MuiDialogTitle>
          <MuiDialogContent>
            {isLoading && <MuiCircularProgress />}
            {!isLoading && (
              <MuiTableContainer component={MuiPaper}>
                <MuiTable aria-label="collapsible table">
                  <MuiTableHead>
                    <MuiTableRow>
                      {columns.map(column => (
                        <MuiTableCell key={column}>{column}</MuiTableCell>
                      ))}
                    </MuiTableRow>
                  </MuiTableHead>
                  <MuiTableBody>
                    <MuiTableRow>
                      <MuiTablePagination
                        rowsPerPageOptions={[25, 50, 100]}
                        rowsPerPage={params.rowsPerPage}
                        count={total || 0}
                        page={params.page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </MuiTableRow>
                    {results?.map((result, index) => (
                      <MuiTableRow key={index}>
                        {columns.map(column => (
                          <MuiTableCell key={column}>
                            <MuiTypography variant="body2">{formatValue(result[column])}</MuiTypography>
                          </MuiTableCell>
                        ))}
                      </MuiTableRow>
                    ))}
                    <MuiTableRow>
                      <MuiTablePagination
                        rowsPerPageOptions={[25, 50, 100]}
                        rowsPerPage={params.rowsPerPage}
                        count={total || 0}
                        page={params.page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </MuiTableRow>
                  </MuiTableBody>
                </MuiTable>
              </MuiTableContainer>
            )}
          </MuiDialogContent>
        </MuiDialog>
      </MuiBox>
    </MuiBox>
  );
};

export default withStyles(styles)(QueryResults);
